import { FAILURE_TYPES_CODE_EXCEPTIONS } from "constants/constants";

/**
 * Failure-detail values shared by the inline table edit and the split modal.
 * One source of truth for the cascade resets and the required-field rule so both
 * surfaces stay in lockstep.
 */
export interface ISegmentFieldValues {
    failure_type_id?: number;
    is_location_specific: boolean;
    area_id?: number;
    functional_location_id?: number;
    failure_code_id?: number;
    user_id?: number;
    notes?: string;
}

export const createEmptySegmentFieldValues = (): ISegmentFieldValues => ({
    is_location_specific: false,
});

// Cascade resets — selecting a parent field clears the dependent ones, mirroring
// the handlers in the original inline-edit dropdowns.
export const applyFailureTypeChange = (
    values: ISegmentFieldValues,
    failureTypeId: number,
    isLocationSpecific: boolean,
): ISegmentFieldValues => ({
    ...values,
    failure_type_id: failureTypeId,
    is_location_specific: isLocationSpecific,
    area_id: undefined,
    functional_location_id: undefined,
    failure_code_id: undefined,
});

export const applyAreaChange = (values: ISegmentFieldValues, areaId: number): ISegmentFieldValues => ({
    ...values,
    area_id: areaId,
    functional_location_id: undefined,
    failure_code_id: undefined,
});

export const applyFunctionalLocationChange = (
    values: ISegmentFieldValues,
    functionalLocationId: number,
): ISegmentFieldValues => ({
    ...values,
    functional_location_id: functionalLocationId,
    failure_code_id: undefined,
});

export const applyFailureCodeChange = (values: ISegmentFieldValues, failureCodeId: number): ISegmentFieldValues => ({
    ...values,
    failure_code_id: failureCodeId,
});

export const applyOperatorChange = (values: ISegmentFieldValues, userId: number): ISegmentFieldValues => ({
    ...values,
    user_id: userId,
});

export const applyNotesChange = (values: ISegmentFieldValues, notes: string): ISegmentFieldValues => ({
    ...values,
    notes,
});

/**
 * Required-field rule shared by the inline edit (EditionOptionsCell) and the split
 * modal. Mirrors EditionOptionsCell's `requiredFailureFields` / `requiredFieldsAdded`
 * exactly (keyed on area_id presence rather than is_location_specific, which is
 * equivalent because area_id is only ever set for location-specific selections).
 *
 * @param failureTypeIsNotSelectedAndUnchanged inline-edit only: the original event's
 *        failure type is NOT_SELECTED and the manager hasn't changed it. Always false
 *        for split segments.
 */
export const getSegmentRequiredSatisfied = (
    values: Pick<ISegmentFieldValues, "failure_type_id" | "failure_code_id" | "area_id" | "functional_location_id">,
    failureTypeCode: string | null | undefined,
    failureTypeIsNotSelectedAndUnchanged = false,
): boolean => {
    const requiredFailureFields =
        failureTypeCode === FAILURE_TYPES_CODE_EXCEPTIONS.BELOW_TRIGGER ||
        failureTypeIsNotSelectedAndUnchanged ||
        Boolean(values.failure_type_id && values.failure_code_id);

    return values.area_id
        ? Boolean(values.functional_location_id && requiredFailureFields)
        : Boolean(values.failure_type_id && requiredFailureFields);
};
